import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Loader2, Activity, CheckCircle, XCircle, MapPin, FileText, Briefcase, AlertTriangle, ShieldCheck, Building2, Users, TrendingUp, PieChart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RAKEZStatistics() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { language } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/rakez-activities/stats/overview`, { headers });
      setStats(res.data.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      toast.error(language === 'ar' ? 'فشل في جلب الإحصائيات' : 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "blue" }) => {
    const colors = {
      blue: "bg-blue-500/20 text-blue-500",
      green: "bg-emerald-500/20 text-emerald-500",
      red: "bg-red-500/20 text-red-500",
      amber: "bg-amber-500/20 text-amber-500",
      purple: "bg-purple-500/20 text-purple-500"
    };
    
    return (
      <div className="bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e]">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 ${colors[color]} rounded-xl flex items-center justify-center`}>
            <Icon className="h-7 w-7" />
          </div>
          <div>
            <p className="text-sm text-[#808080]">{title}</p>
            <p className="text-2xl font-bold text-white">{value?.toLocaleString()}</p>
            {subtitle && <p className="text-xs text-[#606060]">{subtitle}</p>}
          </div>
        </div>
      </div>
    );
  };

  const ProgressBar = ({ label, value, total, color = "#3b82f6" }) => {
    const percentage = total > 0 ? (value / total * 100) : 0;
    return (
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm text-white truncate max-w-[200px]" title={label}>{label}</span>
          <span className="text-sm text-[#808080]">{value.toLocaleString()}</span>
        </div>
        <div className="h-2 bg-[#0a0a0a] rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          />
        </div>
      </div>
    );
  };

  const PieChartSimple = ({ data, colors }) => {
    if (!data || data.length === 0) return null;
    
    const total = data.reduce((sum, item) => sum + item.count, 0);
    let cumulativePercent = 0;
    
    return (
      <div className="flex items-center gap-6">
        <div className="relative w-32 h-32">
          <svg viewBox="0 0 36 36" className="w-32 h-32 transform -rotate-90">
            {data.map((item, index) => {
              const percent = (item.count / total) * 100;
              const dashArray = `${percent} ${100 - percent}`;
              const dashOffset = -cumulativePercent;
              cumulativePercent += percent;
              
              return (
                <circle
                  key={item.name || `slice-${index}`}
                  cx="18"
                  cy="18"
                  r="15.9155"
                  fill="transparent"
                  stroke={colors[index % colors.length]}
                  strokeWidth="3"
                  strokeDasharray={dashArray}
                  strokeDashoffset={dashOffset}
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-bold text-white">{total.toLocaleString()}</span>
          </div>
        </div>
        <div className="flex-1 space-y-2">
          {data.slice(0, 5).map((item, index) => (
            <div key={item.name || `legend-${index}`} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: colors[index % colors.length] }}
              />
              <span className="text-xs text-[#808080] truncate max-w-[120px]" title={item.name}>
                {item.name}
              </span>
              <span className="text-xs text-white ml-auto">{item.count}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
      </div>
    );
  }

  const chartColors = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16"];

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Glow Effect */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <DashboardHeader 
        showBackButton={true}
        backPath="/control/company-setup/ras-al-khaimah/rakez?view=activities"
      />

      {/* Breadcrumb with border */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
            { label_ar: 'منصة العملاء', label_en: 'Customers Platform', path: '/dashboard?view=cloudgm&platform=customers' },
            { label_ar: 'التحكم والمعلومات (منصة العملاء)', label_en: 'Control & Info (Customers)', path: '/control/cloudgm' },
            { label_ar: 'تأسيس الشركات', label_en: 'Company Setup', path: '/control/company-setup' },
            { label_ar: 'إمارة رأس الخيمة', label_en: 'Ras Al Khaimah', path: '/control/company-setup/ras-al-khaimah' },
            { label_ar: 'راكز', label_en: 'RAKEZ', path: '/control/company-setup/ras-al-khaimah/rakez' },
            { label_ar: 'الأنشطة التجارية', label_en: 'Business Activities', path: '/control/company-setup/ras-al-khaimah/rakez?view=activities' },
            { label_ar: 'الإحصائيات', label_en: 'Statistics', path: null }
          ]} />
        </div>
      </div>

      {/* Main Content */}
      <main className="relative z-10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-purple-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">
                {language === 'ar' ? 'إحصائيات أنشطة راكز' : 'RAKEZ Activities Statistics'}
              </h1>
              <p className="text-sm text-[#71717A]">
                {language === 'ar' ? 'تحليل شامل للأنشطة التجارية' : 'Comprehensive business activities analysis'}
              </p>
            </div>
          </div>

          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              icon={Briefcase}
              title={language === 'ar' ? 'إجمالي الأنشطة' : 'Total Activities'}
              value={stats?.total_activities}
              color="blue"
            />
            <StatCard
              icon={CheckCircle}
              title={language === 'ar' ? 'الأنشطة النشطة' : 'Active Activities'}
              value={stats?.status_breakdown?.active}
              subtitle={`${stats?.status_breakdown?.active_percentage}%`}
              color="green"
            />
            <StatCard
              icon={XCircle}
              title={language === 'ar' ? 'الأنشطة المعطلة' : 'Inactive Activities'}
              value={stats?.status_breakdown?.inactive}
              color="red"
            />
            <StatCard
              icon={AlertTriangle}
              title={language === 'ar' ? 'أنشطة خاصة' : 'Special Activities'}
              value={stats?.special_activities}
              color="amber"
            />
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* License Types */}
            <div className="bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-500" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  {language === 'ar' ? 'أنواع الرخص' : 'License Types'}
                </h2>
              </div>
              <PieChartSimple data={stats?.license_types} colors={chartColors} />
            </div>

            {/* Zones */}
            <div className="bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-emerald-500" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  {language === 'ar' ? 'المناطق' : 'Zones'}
                </h2>
              </div>
              <PieChartSimple data={stats?.zones} colors={["#22c55e", "#3b82f6", "#f59e0b"]} />
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Activity Groups */}
            <div className="bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-amber-500" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  {language === 'ar' ? 'مجموعات الأنشطة (أعلى 15)' : 'Activity Groups (Top 15)'}
                </h2>
              </div>
              <div className="space-y-1 max-h-[300px] overflow-y-auto custom-scrollbar">
                {stats?.activity_groups?.map((group, index) => (
                  <ProgressBar
                    key={group.name || `group-${index}`}
                    label={group.name}
                    value={group.count}
                    total={stats?.total_activities}
                    color={chartColors[index % chartColors.length]}
                  />
                ))}
              </div>
            </div>

            {/* GICS Sectors */}
            <div className="bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-purple-500" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  {language === 'ar' ? 'قطاعات GICS' : 'GICS Sectors'}
                </h2>
              </div>
              <div className="space-y-1 max-h-[300px] overflow-y-auto custom-scrollbar">
                {stats?.gics_sectors?.map((sector, index) => (
                  <ProgressBar
                    key={sector.name || `gics-${index}`}
                    label={sector.name}
                    value={sector.count}
                    total={stats?.total_activities}
                    color={chartColors[index % chartColors.length]}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Charts Row 3 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Risk Ratings */}
            <div className="bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-red-500" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  {language === 'ar' ? 'تقييم مخاطر الامتثال' : 'Compliance Risk Ratings'}
                </h2>
              </div>
              <div className="space-y-3">
                {stats?.risk_ratings?.map((rating, index) => (
                  <div key={rating.name || `risk-${index}`} className="flex items-center justify-between p-3 bg-[#0a0a0a] rounded-xl">
                    <span className="text-sm text-white">{rating.name || 'N/A'}</span>
                    <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                      rating.name?.toLowerCase().includes('low') ? 'bg-emerald-500/20 text-emerald-400' :
                      rating.name?.toLowerCase().includes('high') ? 'bg-red-500/20 text-red-400' :
                      'bg-amber-500/20 text-amber-400'
                    }`}>
                      {rating.count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* HSE Classifications */}
            <div className="bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                  <Activity className="h-5 w-5 text-cyan-500" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  {language === 'ar' ? 'تصنيفات HSE' : 'HSE Classifications'}
                </h2>
              </div>
              <div className="space-y-3">
                {stats?.hse_classifications?.map((hse, index) => (
                  <div key={hse.name || `hse-${index}`} className="flex items-center justify-between p-3 bg-[#0a0a0a] rounded-xl">
                    <span className="text-sm text-white truncate max-w-[150px]" title={hse.name}>{hse.name || 'N/A'}</span>
                    <span className="px-3 py-1 rounded-lg text-sm font-medium bg-cyan-500/20 text-cyan-400">
                      {hse.count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Stats */}
            <div className="bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-pink-500/20 rounded-xl flex items-center justify-center">
                  <PieChart className="h-5 w-5 text-pink-500" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  {language === 'ar' ? 'معلومات إضافية' : 'Additional Info'}
                </h2>
              </div>
              <div className="space-y-4">
                <div className="p-4 bg-[#0a0a0a] rounded-xl">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#808080]">
                      {language === 'ar' ? 'أنشطة خاصة' : 'Special Activities'}
                    </span>
                    <span className="text-lg font-bold text-amber-400">{stats?.special_activities?.toLocaleString()}</span>
                  </div>
                </div>
                <div className="p-4 bg-[#0a0a0a] rounded-xl">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#808080]">
                      {language === 'ar' ? 'غير مسموح للعمل المشترك' : 'Not Allowed Coworking'}
                    </span>
                    <span className="text-lg font-bold text-red-400">{stats?.coworking_not_allowed?.toLocaleString()}</span>
                  </div>
                </div>
                <div className="p-4 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white">
                      {language === 'ar' ? 'نسبة التفعيل' : 'Activation Rate'}
                    </span>
                    <span className="text-2xl font-bold text-white">{stats?.status_breakdown?.active_percentage}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #0a0a0a;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #2a2a4e;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #3a3a5e;
        }
      `}</style>
    </div>
  );
}
